import ReactECharts from 'echarts-for-react';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

interface BarChartAnimatedProps {
  data: Array<{ name: string; value: number; fill?: string }>;
  title?: string;
  height?: number;
}

export function BarChartAnimated({ data, title, height = 300 }: BarChartAnimatedProps) {
  const [isDarkMode, setIsDarkMode] = useState(() => 
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        No hay datos disponibles
      </div>
    );
  }

  // Theme-aware color palette
  const colors = {
    foreground: isDarkMode ? '#f1f5f9' : '#0f172a',
    mutedForeground: isDarkMode ? '#cbd5e1' : '#64748b',
    border: isDarkMode ? '#e2e8f0' : '#cbd5e1',
    gridLine: isDarkMode ? '#475569' : '#cbd5e1',
    popover: isDarkMode ? '#1e293b' : '#ffffff',
    popoverForeground: isDarkMode ? '#f1f5f9' : '#0f172a',
  };

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      backgroundColor: colors.popover,
      borderColor: colors.border,
      textStyle: {
        color: colors.popoverForeground
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '5%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: data.map(item => item.name),
      axisLabel: {
        rotate: 45,
        interval: 0,
        fontSize: 11,
        color: colors.foreground
      },
      axisLine: {
        lineStyle: {
          color: colors.border
        }
      },
      axisTick: {
        lineStyle: {
          color: colors.border
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: colors.mutedForeground
      },
      axisLine: {
        lineStyle: {
          color: colors.border
        }
      },
      axisTick: {
        lineStyle: {
          color: colors.border
        }
      },
      splitLine: {
        lineStyle: {
          color: colors.gridLine,
          type: [5, 5],
          width: 1,
        }
      }
    },
    series: [
      {
        type: 'bar',
        data: data.map(item => ({
          value: item.value,
          itemStyle: {
            color: item.fill,
            borderRadius: [8, 8, 0, 0]
          },
          emphasis: {
            itemStyle: {
              color: item.fill,
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowOffsetY: 0,
              shadowColor: isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.2)'
            }
          }
        })),
        animationDelay: (idx: number) => idx * 100,
        animationEasing: 'elasticOut'
      }
    ],
    animationDuration: 800
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      {title && (
        <h3 className="mb-4 text-lg font-semibold text-foreground">{title}</h3>
      )}
      <ReactECharts option={option} style={{ height }} notMerge lazyUpdate />
    </motion.div>
  );
}
